import {notification, Typography} from 'antd';
import React, {useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';

import {NOTIFICATIONS_STREAM} from '../consts/api/apiMethods';
import drawerContentTypes from '../consts/drawer/drawerContentTypes';
import {getTagInfo} from '../consts/notifications/notifications';
import ApiClient from '../services/api/clients/apiClient';
import {showMobileServerNotification} from '../services/app/serverNotificationService';
import {toggleDrawer} from '../state/ducks/drawer';
import {receiveNewNotification} from '../state/ducks/notifications';
import useIsMobileViewport from './useIsMobileViewport';

notification.config({
    maxCount: 3,
});

const useNotificationStream = (enabled = true) => {
    const dispatch = useDispatch();
    const isMobile = useIsMobileViewport();
    const isMobileRef = useRef(isMobile);

    useEffect(() => {
        isMobileRef.current = isMobile;
    }, [isMobile]);

    useEffect(() => {
        if (!enabled) return;

        const apiClient = new ApiClient();

        const connection = apiClient.callSignalR({
            methodName: NOTIFICATIONS_STREAM,
        });

        connection
            .start()
            .then(() => {
                connection.on('ReceiveNotification', (item) => {
                    dispatch(receiveNewNotification(item));

                    window.dispatchEvent(
                        new CustomEvent('onSystemNotification', {
                            detail: item,
                        })
                    );

                    if (isMobileRef.current) {
                        showMobileServerNotification(item, () => {
                            dispatch(toggleDrawer(drawerContentTypes.NOTIFICATIONS));
                        });

                        return;
                    }

                    const {name: tagName, color} = getTagInfo(item.typeCode);
                    const notificationKey = `notif-${item.id}`;

                    notification.open({
                        key: notificationKey,
                        message: null,
                        description: (
                            <div className='flex items-stretch gap-3.5 w-full select-none group mt-[-2px]'>
                                <div
                                    className='flex-shrink-0 w-[4px] rounded-full my-0.5'
                                    style={{backgroundColor: color}}
                                />

                                <div className='flex flex-col flex-1 overflow-hidden pr-6 py-0.5'>
                                    <span
                                        className='text-[10px] font-bold uppercase tracking-[0.05em] mb-1'
                                        style={{color}}
                                    >
                                        {tagName || 'Сповіщення'}
                                    </span>

                                    <Typography.Text className='text-14 font-semibold text-midnight leading-13 truncate mb-1.5'>
                                        {item.title}
                                    </Typography.Text>

                                    <span className='text-[11px] font-medium text-nobel group-hover:text-charcoal transition-colors duration-200'>
                                        Натисніть, щоб переглянути &rarr;
                                    </span>
                                </div>
                            </div>
                        ),
                        placement: 'bottomRight',
                        duration: 5,
                        onClick: () => {
                            dispatch(toggleDrawer(drawerContentTypes.NOTIFICATIONS));
                            notification.destroy(notificationKey);
                        },
                        style: {
                            width: '320px',
                            borderRadius: '12px',
                            padding: '16px',
                            backgroundColor: 'var(--color-white)',
                            border: '1px solid var(--color-whisper)',
                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 4px 10px -6px rgba(0, 0, 0, 0.04)',
                            cursor: 'pointer',
                        },
                    });
                });
            })
            .catch((err) => {
                console.error('SignalR Connection failed:', err);
            });

        return () => {
            connection.off('ReceiveNotification');
            connection.stop();
        };
    }, [enabled, dispatch]);
};

export default useNotificationStream;
